import {
  ApprovalActionEnum,
  type BulkCourseRegistrationActionInput,
  type BulkRegistrationActionResult,
} from '@/types/composition-functions';

export type { BulkCourseRegistrationActionInput, BulkRegistrationActionResult };

export interface BulkScopeFilters {
  level?: string | null;
  academicCalendarId?: string | null;
  programmeId?: string | null;
  departmentId?: string | null;
}

/**
 * Rows in the approval views are per-student AGGREGATES; the backend's bulk
 * mutation acts on the individual student_course_registrations ids nested
 * inside them. Student ids live in a different id space and must never be
 * sent as registration_ids.
 */
export function registrationIdsFromRows(
  rows: Array<{ registrations?: Array<{ id: string | number }> | null }>,
): string[] {
  return rows.flatMap((row) => (row.registrations ?? []).map((r) => String(r.id)));
}

export function buildSelectionBulkInput(
  action: ApprovalActionEnum,
  registrationIds: string[],
  reason?: string,
): BulkCourseRegistrationActionInput {
  const input: BulkCourseRegistrationActionInput = { action, registration_ids: registrationIds };
  if (action === ApprovalActionEnum.Reject && reason?.trim()) {
    input.reason = reason.trim();
  }
  return input;
}

export function buildScopeBulkInput(
  action: ApprovalActionEnum,
  filters: BulkScopeFilters,
  reason?: string,
): BulkCourseRegistrationActionInput {
  const input: BulkCourseRegistrationActionInput = { action };
  if (filters.level !== null && filters.level !== undefined) {
    input.registration_level = String(filters.level);
  }
  if (filters.programmeId) {
    input.programme_id = filters.programmeId;
  }
  if (filters.departmentId) {
    input.department_id = filters.departmentId;
  }
  if (filters.academicCalendarId) {
    input.academic_calendar_id = filters.academicCalendarId;
  }
  if (action === ApprovalActionEnum.Reject && reason?.trim()) {
    input.reason = reason.trim();
  }
  return input;
}

export function hasScopeCriteria(filters: BulkScopeFilters): boolean {
  return (filters.level !== null && filters.level !== undefined)
    || !!filters.academicCalendarId
    || !!filters.programmeId
    || !!filters.departmentId;
}

export function bulkActionLabel(action: ApprovalActionEnum): string {
  switch (action) {
  case ApprovalActionEnum.Approve: return 'Approve';
  case ApprovalActionEnum.Endorse: return 'Endorse';
  case ApprovalActionEnum.Reject: return 'Reject';
  default: return 'Apply';
  }
}
